import React from 'react';
import { useStore, Product } from '../../store/useStore';
import { Input, Repeater, Textarea, GalleryUpload, DocumentUpload } from '../ui';
import { Package } from 'lucide-react';

export function M4_Products() {
  const { products, setProducts } = useStore();

  const handleAddProduct = () => {
    setProducts([
      ...products,
      {
        id: Date.now().toString(),
        nome: '',
        descricao: '',
        preco: '',
        ticketMedio: '',
        margemEstimada: '',
        tempoEntrega: '',
        prioridade: ''
      }
    ]);
  };

  const handleRemoveProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleChange = (id: string, field: keyof Product, value: string) => {
    setProducts(products.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
        <Repeater
          title="Produtos e Serviços Cadastrados"
          description="Liste todos os produtos, serviços, combos ou upsells que a empresa vende."
          items={products}
          onAdd={handleAddProduct}
          onRemove={handleRemoveProduct}
          addButtonText="Adicionar Produto/Serviço"
          getItemSummary={(item) => ({
            title: item.nome || 'Novo Produto/Serviço',
            subtitle: item.preco ? `Preço base: R$ ${item.preco}` : 'Detalhes incompletos'
          })}
          renderItem={(product) => (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                <Package size={18} className="text-indigo-500" />
                <h4 className="font-semibold text-gray-900">{product.nome || 'Novo Item'}</h4>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <Input 
                    label="Nome do Produto/Serviço" 
                    value={product.nome} 
                    onChange={(e) => handleChange(product.id, 'nome', e.target.value)} 
                    placeholder="Ex: Consultoria Premium" 
                  />
                  <Textarea 
                    label="Descrição Rápida" 
                    value={product.descricao} 
                    onChange={(e) => handleChange(product.id, 'descricao', e.target.value)} 
                    placeholder="O que está incluso?"
                    className="min-h-[110px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative col-span-2 sm:col-span-1">
                    <Input 
                        label="Preço Base" 
                        value={product.preco} 
                        onChange={(e) => handleChange(product.id, 'preco', e.target.value)} 
                        placeholder="0,00" 
                        className="pl-9"
                    />
                    <span className="absolute left-3 top-8 text-gray-500 font-medium text-sm border-r border-gray-200 pr-1">R$</span>
                  </div>
                  <div className="relative col-span-2 sm:col-span-1">
                    <Input 
                        label="Ticket Médio" 
                        value={product.ticketMedio} 
                        onChange={(e) => handleChange(product.id, 'ticketMedio', e.target.value)} 
                        placeholder="0,00" 
                        className="pl-9"
                    />
                    <span className="absolute left-3 top-8 text-gray-500 font-medium text-sm border-r border-gray-200 pr-1">R$</span>
                  </div>
                  <div className="relative col-span-2 sm:col-span-1">
                    <Input 
                        label="Margem Estimada" 
                        value={product.margemEstimada} 
                        onChange={(e) => handleChange(product.id, 'margemEstimada', e.target.value)} 
                        placeholder="0" 
                        className="pr-8"
                    />
                     <span className="absolute right-3 top-8 text-gray-500 font-medium text-sm border-l border-gray-200 pl-1">%</span>
                  </div>
                  <Input 
                    label="Tempo de Entrega" 
                    value={product.tempoEntrega} 
                    onChange={(e) => handleChange(product.id, 'tempoEntrega', e.target.value)} 
                    placeholder="Ex: Imediato, 15 dias..." 
                    className="col-span-2 sm:col-span-1"
                  />
                  <Input 
                    label="Prioridade de Venda" 
                    value={product.prioridade} 
                    onChange={(e) => handleChange(product.id, 'prioridade', e.target.value)} 
                    placeholder="Ex: Alta, Baixa, Esteira..." 
                    className="col-span-2"
                  />
                </div>
              </div>
              <div className="pt-4 border-t border-gray-100 flex flex-col gap-6">
                 <GalleryUpload 
                   label="Galeria de Fotos do Produto/Serviço"
                   description="Mostre como é o produto. Máximo de 10 imagens."
                   onChange={(files) => handleChange(product.id, 'fotos', files as any)}
                 />
                 <DocumentUpload 
                   label="Material de Apoio e Tabelas (PDF)"
                   description="Insira catálogos, tabelas de preço e brochuras para este produto."
                   onChange={(files) => handleChange(product.id, 'documentos', files as any)}
                 />
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}
